import React, { Suspense, useEffect, useCallback } from "react";
import {
  Navigate,
  Route,
  Routes as Router,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useFlagsStatus } from "@unleash/proxy-client-react";
import { ThemeProvider } from "./components/theme-provider";
import { postLog } from "./lib/logs";
import { PageViewLogger } from "./components/page-view-logger";

import { useAppSelector } from "./redux/hooks";
import {
  authSelectors,
  selectAuthenticatedUser,
} from "./redux/slices/auth.slice";

import { useDispatch } from "react-redux";
import * as featureFlag from "./components/feature-flag/feature-flag";
import { OracaoPermissionRoute } from "./components/feature-flag/feature-flag";
import { Loading } from "./layouts/loading";
import { HomeSkeleton } from "@/components/loading/home-skeleton";
import { SetConfiguracaoId } from "./redux/slices/configuracoes.slice";
import { SetEventoId } from "./redux/slices/eventos.slice";
import { LandingPage } from "@/pages/Pricing/LandingPage";
import ScrollToTop from "@/components/ScrollToTop";
import { isSlugDomain } from "@/utils/domain-utils";
import { buildVideoSistemaPath } from "@/lib/devocional-publico-links";
import { InscricaoBootSkeleton } from "@/pages/Inscricao/components/InscricaoBootSkeleton";
import { SiteIgrejaBootSkeleton } from "@/pages/SiteIgreja/components/SiteIgrejaBootSkeleton";
import { PublicLinksSkeleton } from "@/pages/Links/PublicLinksSkeleton";
import {
  isSiteIgrejaPublicPath,
  isSysIgrejaLandingEntryPath,
} from "@/pages/SiteIgreja/site-igreja-routes";
import { LandingBootSkeleton } from "@/pages/SiteIgreja/landing/LandingBootSkeleton";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { getAppearanceState, saveAppearanceState } from "@/lib/appearance-storage";
import {
  isChunkLoadError,
  lazyWithRetry,
  recoverFromChunkLoadError,
} from "@/lib/lazy-with-retry";
import { ErrorBoundary } from "react-error-boundary";

const CheckinEquipePreview = import.meta.env.DEV ? React.lazy(() => import("./pages/Dev/CheckinEquipePreview")) : null;
const HomeEquipeAtualPreview = import.meta.env.DEV ? React.lazy(() => import("./pages/Dev/HomeEquipeAtualPreview")) : null;
const AuditoriaTablePreview = import.meta.env.DEV
  ? React.lazy(() => import("./pages/Dev/AuditoriaTablePreview"))
  : null;

function isDevEnvironment(): boolean {
  const hostname = window.location.hostname;
  return (
    hostname.includes("sysigreja") ||
    hostname.includes("localhost") ||
    hostname.startsWith("192.168.") ||
    hostname.startsWith("10.") ||
    hostname.startsWith("172.")
  );
}

type FormInscricaoRouteProps = { type: "voluntário" | "participante" };

const FormInscricao = lazyWithRetry(
  () => import("./pages/Inscricao/FormInscricao"),
  "FormInscricao",
  "FormInscricaoPage",
) as React.LazyExoticComponent<React.ComponentType<FormInscricaoRouteProps>>;
const Inscricoes = lazyWithRetry(
  () => import("./pages/Inscricao/Inscricoes"),
  "Inscricoes",
);
const PublicEntryShell = lazyWithRetry(
  () => import("./pages/SiteIgreja/PublicEntryShell"),
  "PublicEntryShell",
  "PublicEntryShell",
);
const EnsinoPermissionRouteConditional = React.lazy(
  () => import("./components/feature-flag/ensino-permission-route-conditional"),
);
const PublicHomeGate = React.lazy(() => import("./pages/SiteIgreja/PublicHomeGate"));
const SiteIgrejaEventos = React.lazy(() => import("./pages/SiteIgreja/Eventos"));
const SiteIgrejaGrupos = React.lazy(() => import("./pages/SiteIgreja/Grupos"));
const SiteIgrejaEnsino = React.lazy(() => import("./pages/SiteIgreja/Ensino"));
const SiteIgrejaComunidade = React.lazy(() => import("./pages/SiteIgreja/Comunidade"));
const SiteIgrejaBlog = React.lazy(() => import("./pages/SiteIgreja/landing/SysIgrejaBlog"));
const SiteIgrejaBiblia = React.lazy(() => import("./pages/SiteIgreja/Biblia"));
const SiteIgrejaCadastroMembros = React.lazy(
  () => import("./pages/SiteIgreja/CadastroMembros"),
);
const SiteIgrejaEditor = React.lazy(
  () => import("./pages/SiteIgreja/admin/SiteIgrejaEditor"),
);
const Login = React.lazy(() => import("./pages/Login"));
const Trial = React.lazy(() => import("./pages/Trial"));
const Cadastro = React.lazy(() => import("./pages/Cadastro"));
const LpTrial = React.lazy(() => import("./pages/LpTrial"));
const CompararSysigrejaPage = React.lazy(
  () => import("./pages/Comparar/CompararSysigrejaPage"),
);
const RecuperarSenha = React.lazy(() => import("./pages/RecuperarSenha"));
const NewUser = React.lazy(() => import("./pages/NewUser"));
const NewPassword = React.lazy(() => import("./pages/NewPassword"));
const Checkin = React.lazy(() => import("./pages/Checkin/Checkin"));
const Detalhes = lazyWithRetry(
  () => import("./pages/Inscricao/Detalhes"),
  "Detalhes",
);
const DetalhesCurso = lazyWithRetry(
  () => import("./pages/Inscricao/DetalhesCurso"),
  "DetalhesCurso",
);
const InscricaoConcluida = lazyWithRetry(
  () => import("./pages/Inscricao/InscricaoConcluida"),
  "InscricaoConcluida",
);
const InscricaoEncerrada = lazyWithRetry(
  () => import("./pages/Inscricao/InscricaoEncerrada"),
  "InscricaoEncerrada",
);
const LayoutInscricao = lazyWithRetry(
  () => import("./layouts/_inscricao"),
  "LayoutInscricao",
);
const LayoutLinks = React.lazy(() => import("./layouts/_links"));
const EcommerceRouteGuard = React.lazy(
  () =>
    import("./layouts/ecommerce-route-guard").then((m) => ({
      default: m.EcommerceRouteGuard,
    })),
);
const Layout = React.lazy(() => import("./layouts/_main"));
const PedidosOracaoPublic = React.lazy(() => import("./pages/PedidosOracao/PedidosOracaoPublic"));
const ListagemPedidosOracao = React.lazy(() => import("./pages/PedidosOracao/ListagemPedidosOracao"));
const Programacao = React.lazy(() => import("./pages/Programacao/Programacao"));
const MomentoCoordenador = React.lazy(() => import("./pages/Programacao/MomentoCoordenador"));
const Reunioes = React.lazy(
  () => import("./pages/Cadastros/Reunioes/Reunioes"),
);
const Produtos = React.lazy(
  () => import("./pages/Patrimonio/Produtos/Produtos"),
);
const NaoAutorizado = React.lazy(() => import("./pages/NaoAutorizado"));

/** Leva os links antigos (menu, home, favoritos) para a aba correspondente em Participantes. */
function RedirecionarParticipantesAba({ aba }: { aba: string }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("aba", aba);
  params.delete("modulo");
  return <Navigate to={`/sistema/participantes?${params.toString()}`} replace />;
}

/** Leva os links antigos (menu, home, favoritos) para a aba correspondente em Voluntários. */
function RedirecionarVoluntariosAba({ aba }: { aba: string }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("aba", aba);
  params.delete("modulo");
  return <Navigate to={`/sistema/voluntarios?${params.toString()}`} replace />;
}

/** Participantes e voluntários: telas antigas de distribuição viram abas da listagem. */
function RedirecionarDistribuicao({ pessoas, modulo }: { pessoas: "participantes" | "voluntarios"; modulo: string }) {
  if (pessoas === "participantes") return <RedirecionarParticipantesAba aba={modulo} />;
  return <RedirecionarVoluntariosAba aba={modulo} />;
}

function DistribuicaoParticipantesRoute() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const modulo = params.get("modulo");
  if (modulo) {
    params.set("aba", modulo);
    params.delete("modulo");
  }
  return <Navigate to={`/sistema/participantes?${params.toString()}`} replace />;
}

function DistribuicaoVoluntariosRoute() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const modulo = params.get("modulo");
  params.set("aba", modulo === "equipes" ? "equipes" : "quartos");
  params.delete("modulo");
  return <Navigate to={`/sistema/voluntarios?${params.toString()}`} replace />;
}
const Home = React.lazy(() => import("./pages/Home"));
const Participantes = React.lazy(
  () => import("./pages/Participantes/Participantes"),
);
const ListagemParticipantes = React.lazy(
  () => import("./pages/Participantes/Listagem/listagem-participantes"),
);
const ListagemGeralAlunos = React.lazy(
  () => import("./pages/Participantes/Listagem/listagem-geral-alunos"),
);
const CentroCusto = React.lazy(
  () => import("./pages/Financeiro/CentroCusto/CentroCusto"),
);
const Voluntarios = React.lazy(() => import("./pages/Voluntarios/Voluntarios"));
const Credenciais = React.lazy(
  () => import("./pages/Cadastros/Credenciais/Credenciais"),
);
const Extrato = React.lazy(() => import("./pages/Financeiro/Extrato/Extrato"));
const FinanceiroGeral = React.lazy(() => import("./pages/Financeiro/FinanceiroGeral/FinanceiroGeral"));
const FormaPagamento = React.lazy(
  () => import("./pages/Financeiro/FormaPagamento/FormaPagamento"),
);
const Marcadores = React.lazy(
  () => import("./pages/Cadastros/Marcadores/Marcadores"),
);
const Mensagens = React.lazy(
  () => import("./pages/Cadastros/Mensagens/Mensagens"),
);
const Eventos = React.lazy(() => import("./pages/Cadastros/Eventos/Eventos"));
const Cupons = React.lazy(() => import("./pages/Cadastros/Eventos/cupons"));
const Turmas = React.lazy(() => import("./pages/Cadastros/Turmas/Turmas"));
const PequenosGrupos = React.lazy(() => import("./pages/Cadastros/PequenosGrupos/PequenosGrupos"));
const Disciplinas = React.lazy(() => import("./pages/Cadastros/Disciplinas/Disciplinas"));
const Liderancas = React.lazy(() => import("./pages/Cadastros/Liderancas/Liderancas"));
const ArvoreLiderancas = React.lazy(() => import("./pages/Cadastros/Liderancas/ArvoreLiderancas"));
const ListagemLiderancas = React.lazy(() => import("./pages/Cadastros/Liderancas/ListagemLiderancas"));
const Jornada = React.lazy(() => import("./pages/Cadastros/Jornada/Jornada"));
const Templates = React.lazy(() => import("./pages/Avaliacoes/Templates/Templates"));
const ListagemAvaliacoes = React.lazy(() => import("./pages/Avaliacoes/Listagem/Listagem"));
const Parametros = React.lazy(
  () => import("./pages/Configuracoes/Parametros/Parametros"),
);
const Agenda = React.lazy(() => import("./pages/Administracao/Agenda"));
const Locais = React.lazy(() => import("./pages/Administracao/Locais"));
const Informativos = React.lazy(() => import("./pages/Administracao/Informativos/Informativos"));
const Organizadores = React.lazy(
  () => import("./pages/Configuracoes/Organizadores/Organizadores"),
);
const Organizacao = React.lazy(
  () => import("./pages/Configuracoes/Organizacao/Organizacao"),
);
const PerfilInstituicao = React.lazy(
  () => import("./pages/Configuracoes/PerfilInstituicao/PerfilInstituicao"),
);
const Ajuda = React.lazy(() => import("./pages/Ajuda"));
const NotificacoesCentral = React.lazy(() => import("./pages/Notificacoes/NotificacoesCentral"));
const NotificacoesPushPage = React.lazy(() => import("./pages/NotificacoesPush/NotificacoesPushPage"));
const ConquistasCentral = React.lazy(() => import("./pages/Conquistas/ConquistasCentral"));
const Aniversariantes = React.lazy(
  () => import("./pages/Relatorios/Aniversariantes/Aniversariantes"),
);
const RestricaoAlimentar = React.lazy(
  () => import("./pages/Relatorios/RestricaoAlimentar/RestricaoAlimentar"),
);
const Financeiro = React.lazy(
  () => import("./pages/Relatorios/Financeiro/Financeiro"),
);
const Listagem = React.lazy(() => import("./pages/Listagem/Listagem"));
const BuscaGlobalPage = React.lazy(() => import("./pages/BuscaGlobal/busca-global-page"));
const Ministerios = React.lazy(() => import("./pages/Administracao/Ministerios/Ministerios"));
const MinisterioDetalhe = React.lazy(() => import("./pages/Administracao/Ministerios/MinisterioDetalhe"));
const Arquivos = React.lazy(() => import("./pages/Administracao/Arquivos/Arquivos"));
const Campos = React.lazy(() => import("./pages/Administracao/Campos"));
const Privacidade = React.lazy(() => import("./pages/Privacidade"));
const Termos = React.lazy(() => import("./pages/Termos"));
const AppDownload = React.lazy(() => import("./pages/AppDownload"));
const DevocionalPublico = React.lazy(() => import("./pages/DevocionalPublico"));
const LancamentoAppRsvp = React.lazy(
  () => import("./pages/LancamentoAppRsvp"),
);
const Vendas = React.lazy(() => import("./pages/Vendas/Vendas"));
const OperacaoCultoInfantil = React.lazy(
  () => import("./pages/Infantil/OperacaoCulto"),
);
const CadastroFamiliaInfantil = React.lazy(
  () => import("./pages/Infantil/CadastroFamiliaInfantil"),
);
const ListagemCriancasInfantil = React.lazy(
  () => import("./pages/Infantil/ListagemCriancasInfantil"),
);
const MomentosCultoInfantil = React.lazy(
  () => import("./pages/Infantil/MomentosCultoInfantil"),
);
const InfantilFamiliaPage = React.lazy(
  () => import("./pages/Infantil/InfantilFamiliaPage"),
);
const Voluntariado = React.lazy(() => import("./pages/Relatorios/Voluntariado/Voluntariado"));
const Links = React.lazy(() => import("./pages/Configuracoes/Links/Links"));
const PublicLinksPage = React.lazy(() => import("./pages/Links/PublicLinksPage"));
const Backoffice = React.lazy(() => import("./pages/Backoffice/Backoffice"));
const Newsletter = React.lazy(() => import("./pages/Newsletter"));
const NewsletterArtigo = React.lazy(() => import("./pages/NewsletterArtigo"));
const Cursos = React.lazy(() => import("./pages/Ensino/Cursos/Cursos"));
const Certificados = React.lazy(() => import("./pages/Ensino/Certificados/Certificados"));
const OAuthCallback = React.lazy(() => import("./pages/MercadoPago/OAuthCallback"));
const MercadoPagoConnect = React.lazy(() => import("./pages/MercadoPago/MercadoPagoConnect"));
const SsoCallback = React.lazy(() => import("./pages/SsoCallback"));
const PagamentoSucesso = React.lazy(() => import("./pages/Pagamento/PagamentoSucesso"));
const PagamentoPendente = React.lazy(() => import("./pages/Pagamento/PagamentoPendente"));
const PagamentoErro = React.lazy(() => import("./pages/Pagamento/PagamentoErro"));
const PagamentoStatus = React.lazy(() => import("./pages/Pagamento/PagamentoStatus"));
const PagamentoMensalidadePix = React.lazy(() => import("./pages/Pagamento/PagamentoMensalidadePix"));
const PlusPage = React.lazy(() => import("./pages/Plus/PlusPage"));
const QuestionariosListaPage = React.lazy(() => import("./pages/Questionarios/admin/QuestionariosListaPage"));
const QuestionarioDetalhePage = React.lazy(() => import("./pages/Questionarios/admin/QuestionarioDetalhePage"));
const QuestionariosLgpdPage = React.lazy(() => import("./pages/Questionarios/admin/QuestionariosLgpdPage"));
const QuestionariosCuponsPage = React.lazy(() => import("./pages/Questionarios/admin/QuestionariosCuponsPage"));
const QuizPublicoPage = React.lazy(() => import("./pages/Questionarios/publico/QuizPublicoPage"));
const PlusSucesso = React.lazy(() => import("./pages/Plus/PlusSucesso"));
const PlusAssinaturasPage = React.lazy(() => import("./pages/Plus/gestao/PlusAssinaturasPage"));
const PlusPlanosPage = React.lazy(() => import("./pages/Plus/gestao/PlusPlanosPage"));
const PlusCuponsPage = React.lazy(() => import("./pages/Plus/gestao/PlusCuponsPage"));
const PlusClubePage = React.lazy(() => import("./pages/Plus/gestao/PlusClubePage"));
const PlusCampanhasPage = React.lazy(() => import("./pages/Plus/gestao/PlusCampanhasPage"));
const ListaProdutos = React.lazy(() => import("./pages/Ecommerce/ListaProdutos"));
const DetalheProduto = React.lazy(() => import("./pages/Ecommerce/DetalheProduto"));
const Checkout = React.lazy(() => import("./pages/Ecommerce/Checkout"));
const CheckoutSucesso = React.lazy(() => import("./pages/Ecommerce/CheckoutSucesso"));
const Indicadores = React.lazy(() => import("./pages/Indicadores/Indicadores"));
const Devocionais = React.lazy(() => import("./pages/Devocionais/Devocionais"));
const Grupos = React.lazy(() => import("./pages/Devocionais").then(module => ({ default: module.Grupos })));
const DetalhesGrupo = React.lazy(() => import("./pages/Devocionais/Grupos/DetalhesGrupo"));
const PlanosLeituraParidade = React.lazy(() => import("./pages/Devocionais/Planos/PlanosLeituraParidade"));
const DetalhesCheckin = React.lazy(() => import("./pages/Devocionais/Checkins/DetalhesCheckin"));
const MeusCheckinsParidade = React.lazy(() => import("./pages/Devocionais/MeusCheckinsParidade"));
const DevocionalVideoPage = React.lazy(() => import("./pages/Devocionais/Videos/DevocionalVideoPage"));
const DetalhesTurma = React.lazy(() => import("./pages/Ensino/DetalhesTurma"));
const BibliaPassagem = React.lazy(() => import("./pages/Biblia/BibliaPassagem"));


const PrivateRoutes = (): React.ReactElement | null => {
  const { access_token } = useAppSelector(selectAuthenticatedUser);
  const location = useLocation();
  const temaEscuroWebFlag = useFeatureFlag("TemaEscuroWeb");
  const { flagsReady } = useFlagsStatus();
  const persistedDarkAvailable = getAppearanceState().darkAvailable === true;
  // Enquanto o Unleash não hidrata, reusa o hint local para não forçar claro e
  // desfazer o dark aplicado no boot do index.html.
  const temaEscuroWeb = temaEscuroWebFlag || (!flagsReady && persistedDarkAvailable);

  useEffect(() => {
    if (!flagsReady || temaEscuroWebFlag) return;
    const current = getAppearanceState();
    if (current.darkAvailable) {
      saveAppearanceState({ ...current, darkAvailable: false });
    }
  }, [flagsReady, temaEscuroWebFlag]);

  // Se não está logado, salva a rota atual para redirecionamento posterior
  if (!access_token) {
    const currentPath = location.pathname + location.search;
    // Só salva se não for a rota de login para evitar loops
    if (currentPath !== '/login') {

      localStorage.setItem('redirectAfterLogin', currentPath);
    }
    return <Navigate to="/login" />;
  }

  return (
    <ThemeProvider allowDark={temaEscuroWeb}>
      <Layout />
    </ThemeProvider>
  );
};

function handleHome() {
  switch (true) {
    default:
      return <Home />;
  }
}

function RouteSuspenseFallback() {
  const location = useLocation();
  const siteDaIgrejaFlag = useFeatureFlag("SiteDaIgreja");
  const { flagsReady, flagsError } = useFlagsStatus();
  const siteDaIgrejaPending = !siteDaIgrejaFlag && !flagsReady && !flagsError;

  if (location.pathname.startsWith("/sistema")) {
    return <HomeSkeleton />;
  }

  if (location.pathname === "/app" || location.pathname.startsWith("/app/")) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[#07111f]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-sky-300" />
      </div>
    );
  }

  if (
    (siteDaIgrejaFlag || siteDaIgrejaPending) &&
    isSysIgrejaLandingEntryPath(location.pathname)
  ) {
    return <LandingBootSkeleton />;
  }

  if (siteDaIgrejaFlag && isSiteIgrejaPublicPath(location.pathname)) {
    return <SiteIgrejaBootSkeleton />;
  }

  if (!siteDaIgrejaFlag && isSiteIgrejaPublicPath(location.pathname)) {
    return <InscricaoBootSkeleton />;
  }

  const pathSegments = location.pathname.split("/").filter(Boolean);
  if (pathSegments[0] === "links" || pathSegments[1] === "links") {
    return <PublicLinksSkeleton />;
  }

  return <Loading />;
}

function ChunkLoadFallback() {
  return <InscricaoBootSkeleton />;
}

function handleRouteChunkError(error: Error) {
  if (isChunkLoadError(error)) {
    recoverFromChunkLoadError({ origem: "Routes_ErrorBoundary" });
  }
}

/**
 * Layout de inscrição com fronteira de Suspense própria: enquanto o chunk do
 * layout carrega, exibe o skeleton de boot (em vez do spinner genérico de
 * topo), mantendo o carregamento consistente com o resto do funil.
 */
function InscricaoLayoutWithBoundary() {
  return (
    <ErrorBoundary
      FallbackComponent={ChunkLoadFallback}
      onError={handleRouteChunkError}
    >
      <Suspense fallback={<InscricaoBootSkeleton />}>
        <LayoutInscricao />
      </Suspense>
    </ErrorBoundary>
  );
}

function EnsinoHomeRedirect() {
  const [searchParams] = useSearchParams();
  const next = new URLSearchParams(searchParams);
  next.set("mode", "ensino");
  const qs = next.toString();
  return <Navigate to={qs ? `/sistema?${qs}` : "/sistema?mode=ensino"} replace />;
}

/** Em produção o SSR já redireciona `/d/:id` (e conta o clique); aqui cobre dev e navegação SPA. */
function DevocionalLinkCurto() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (!params.get("utm_source")) {
    params.set("utm_source", "youtube");
    params.set("utm_medium", "shorts");
    params.set("utm_campaign", `dev-${id}`);
  }
  return <Navigate to={`/devocional/${id}?${params.toString()}`} replace />;
}

/** Link compartilhado do vídeo do Devocional: abre a tela cheia logada (login antes, se preciso). */
function DevocionalVideoLinkCompartilhado() {
  const { id } = useParams<{ id: string }>();
  const usuario = useAppSelector(selectAuthenticatedUser);
  const videoId = Number(id);
  if (!Number.isFinite(videoId) || videoId <= 0) return <Navigate to="/" replace />;
  const destino = buildVideoSistemaPath(videoId, { compartilhado: true });
  if (usuario?.id) return <Navigate to={destino} replace />;
  return <Navigate to={`/login?redirect=${encodeURIComponent(destino)}`} replace />;
}

export function Routes() {
  const location = useLocation();
  const dispatch = useDispatch();
  // Rodada 3: sem o AnimatePresence lazy em volta das rotas (o <Router> nao tem key, entao nao havia
  // animacao de saida; so baixava o framer-motion e suspendia o primeiro render ate ele chegar).

  useEffect(() => {
    switch (location.pathname) {
      case "/sistema/organizadores":
      case "/sistema/organizacao":
      case "/sistema/login":
      case "/sistema/listagem":
      case "/sistema/ajuda":
        dispatch(SetEventoId(null));
        dispatch(SetConfiguracaoId(null));
        break;

      default:
        break;
    }
  }, [location, dispatch]);

  const routerContent = (
    <Router location={location}>
      {import.meta.env.DEV && CheckinEquipePreview && <Route path="/e2e-equipe-checkin" element={<Suspense fallback={<Loading />}><CheckinEquipePreview /></Suspense>} />}
      {import.meta.env.DEV && HomeEquipeAtualPreview && <Route path="/e2e-home-equipe-atual" element={<Suspense fallback={<Loading />}><HomeEquipeAtualPreview /></Suspense>} />}
      {import.meta.env.DEV && AuditoriaTablePreview && <Route path="/e2e-auditoria" element={<Suspense fallback={<Loading />}><AuditoriaTablePreview /></Suspense>} />}
      <Route path="/login" element={<Login />} />
      <Route path="/login/:id" element={<Login />} />
      <Route path="/trial" element={<Trial />} />
      <Route path="/cadastro" element={<Cadastro />} />
      <Route path="lp" element={<LandingPage />} />
      <Route path="lp/trial" element={<Navigate to="/campanha/trial" replace />} />
      <Route path="campanha/trial" element={<LpTrial />} />
      <Route path="lp/contato" element={<LandingPage />} />
      <Route path="sobre" element={<LandingPage />} />
      <Route path="/" element={<PublicEntryShell />}>
        <Route index element={<PublicHomeGate />} />
        <Route path="eventos" element={<SiteIgrejaEventos />} />
        <Route path="grupos" element={<SiteIgrejaGrupos />} />
        <Route path="ensino" element={<SiteIgrejaEnsino />} />
        <Route path="comunidade" element={<SiteIgrejaComunidade />} />
        <Route path="comunidade/:slug" element={<SiteIgrejaComunidade />} />
        <Route path="artigos" element={<SiteIgrejaComunidade />} />
        <Route path="artigos/:slug" element={<SiteIgrejaComunidade />} />
        <Route path="blog" element={<SiteIgrejaBlog />} />
        <Route path="blog/:slug" element={<SiteIgrejaBlog />} />
        <Route path="biblia" element={<SiteIgrejaBiblia />} />
        {/* Entrada dos devocionais: mesmo catálogo de planos do /biblia (301 de id inexistente cai aqui). */}
        <Route path="devocional" element={<SiteIgrejaBiblia />} />
        <Route path="cadastro-membros" element={<SiteIgrejaCadastroMembros />} />
      </Route>
      {isDevEnvironment() && !isSlugDomain() && (
        <Route path="/:login" element={<PublicEntryShell />}>
          <Route index element={<PublicHomeGate />} />
          <Route path="eventos" element={<SiteIgrejaEventos />} />
          <Route path="grupos" element={<SiteIgrejaGrupos />} />
          <Route path="ensino" element={<SiteIgrejaEnsino />} />
          <Route path="comunidade" element={<SiteIgrejaComunidade />} />
          <Route path="comunidade/:slug" element={<SiteIgrejaComunidade />} />
          <Route path="artigos" element={<SiteIgrejaComunidade />} />
          <Route path="artigos/:slug" element={<SiteIgrejaComunidade />} />
          <Route path="blog" element={<SiteIgrejaBlog />} />
          <Route path="blog/:slug" element={<SiteIgrejaBlog />} />
          <Route path="biblia" element={<SiteIgrejaBiblia />} />
          <Route path="cadastro-membros" element={<SiteIgrejaCadastroMembros />} />
        </Route>
      )}

      {/* Rotas para páginas de links públicas
          Subdomínio (recon.sysigreja.com/links) e path (sysigreja.com/recon/links) */}
      <Route path="/links" element={<LayoutLinks />}>
        <Route path="" element={<PublicLinksPage />} />
        <Route path=":slug" element={<PublicLinksPage />} />
      </Route>
      <Route path="/:login/links" element={<LayoutLinks />}>
        <Route path="" element={<PublicLinksPage />} />
        <Route path=":slug" element={<PublicLinksPage />} />
      </Route>

      {/* Rotas para pedidos de oração: full page sem login; com login = lista de inscrições + sheet */}
      <Route path="/pedidos-oracao" element={<Inscricoes />} />
      {isDevEnvironment() && !isSlugDomain() && (
        <Route path="/:login/pedidos-oracao" element={<Inscricoes />} />
      )}

      {/* Rotas para retorno de pagamento do Mercado Pago */}
      <Route path="/pagamento/sucesso" element={<PagamentoSucesso />} />
      <Route path="/pagamento/pendente" element={<PagamentoPendente />} />
      <Route path="/pagamento/erro" element={<PagamentoErro />} />
      <Route path="/pagamento/status/:preferenceId" element={<PagamentoStatus />} />
      <Route path="/pagamento/mensalidade/:lancamentoId" element={<PagamentoMensalidadePix />} />

      <Route path="/plus" element={<PlusPage />} />
      <Route path="/plus/sucesso" element={<PlusSucesso />} />

      {/* #933 Questionários públicos (ex.: Quem Sou Eu Servindo) */}
      <Route path="/q/:slug" element={<QuizPublicoPage />} />
      <Route path="/q/:slug/r/:uuid" element={<QuizPublicoPage />} />

      {/* Rotas para e-commerce público */}
      {isDevEnvironment() && !isSlugDomain() && (
        <Route path="/ecommerce/:loginSlug" element={<EcommerceRouteGuard />}>
          <Route index element={<Navigate to="produtos" replace />} />
          <Route path="produtos" element={<ListaProdutos />} />
          <Route path="produtos/:id" element={<DetalheProduto />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="checkout/sucesso" element={<CheckoutSucesso />} />
        </Route>
      )}

      {/* Rotas para inscrições: slug no domínio (recon.iecbeventos.com.br) vs login no path (sysigreja.com) */}
      {isSlugDomain() ? (
        <>
          {/* Novo formato: /identificador ou /identificador/inscricoes */}
          <Route path="/:identificador" element={<InscricaoLayoutWithBoundary />}>
            <Route path="cursos" element={<DetalhesCurso />} />
            <Route path="cursos/pagamento/:participanteId" element={<DetalhesCurso />} />
            <Route path="inscricoes" element={<Detalhes />} />
            <Route index element={<Detalhes />} />
            <Route path="informativos" element={<Detalhes />} />
            <Route
              path="inscricoes-encerradas"
              element={<InscricaoEncerrada />}
            />
            <Route
              path="inscricoes-em-breve"
              element={<InscricaoEncerrada />}
            />
            <Route
              path="inscricoes/:type/inscricao-concluida/:id"
              element={<InscricaoConcluida />}
            />
            <Route
              path=":type/inscricao-concluida/:id"
              element={<InscricaoConcluida />}
            />
            <Route
              path="inscricoes/voluntario"
              element={<FormInscricao type="voluntário" />}
            />
            <Route
              path="voluntario"
              element={<FormInscricao type="voluntário" />}
            />
            <Route
              path="inscricoes/participante"
              element={<FormInscricao type="participante" />}
            />
            <Route
              path="participante"
              element={<FormInscricao type="participante" />}
            />
            <Route
              path="inscricoes/participante/pagamento/:participanteId"
              element={<FormInscricao type="participante" />}
            />
            <Route
              path="participante/pagamento/:participanteId"
              element={<FormInscricao type="participante" />}
            />
            <Route
              path="inscricoes/voluntario/pagamento/:participanteId"
              element={<FormInscricao type="voluntário" />}
            />
            <Route
              path="voluntario/pagamento/:participanteId"
              element={<FormInscricao type="voluntário" />}
            />
            <Route path="*" element={<NaoAutorizado />} />
          </Route>
          {/* Retro: /Login com espaços/identificador/inscricoes (QR codes antigos) */}
          <Route path="/:login/:identificador/" element={<InscricaoLayoutWithBoundary />}>
            <Route path="cursos" element={<DetalhesCurso />} />
            <Route path="cursos/pagamento/:participanteId" element={<DetalhesCurso />} />
            <Route path="inscricoes" element={<Detalhes />} />
            <Route index element={<Detalhes />} />
            <Route path="informativos" element={<Detalhes />} />
            <Route
              path="inscricoes-encerradas"
              element={<InscricaoEncerrada />}
            />
            <Route
              path="inscricoes-em-breve"
              element={<InscricaoEncerrada />}
            />
            <Route
              path="inscricoes/:type/inscricao-concluida/:id"
              element={<InscricaoConcluida />}
            />
            <Route
              path=":type/inscricao-concluida/:id"
              element={<InscricaoConcluida />}
            />
            <Route
              path="inscricoes/voluntario"
              element={<FormInscricao type="voluntário" />}
            />
            <Route
              path="voluntario"
              element={<FormInscricao type="voluntário" />}
            />
            <Route
              path="inscricoes/participante"
              element={<FormInscricao type="participante" />}
            />
            <Route
              path="participante"
              element={<FormInscricao type="participante" />}
            />
            <Route
              path="inscricoes/participante/pagamento/:participanteId"
              element={<FormInscricao type="participante" />}
            />
            <Route
              path="participante/pagamento/:participanteId"
              element={<FormInscricao type="participante" />}
            />
            <Route
              path="inscricoes/voluntario/pagamento/:participanteId"
              element={<FormInscricao type="voluntário" />}
            />
            <Route
              path="voluntario/pagamento/:participanteId"
              element={<FormInscricao type="voluntário" />}
            />
            <Route path="*" element={<NaoAutorizado />} />
          </Route>
        </>
      ) : (
        <Route path="/:login/:identificador/" element={<InscricaoLayoutWithBoundary />}>
          <Route path="cursos" element={<DetalhesCurso />} />
          <Route path="cursos/pagamento/:participanteId" element={<DetalhesCurso />} />
          <Route path="inscricoes" element={<Detalhes />} />
          <Route index element={<Detalhes />} />
          <Route path="informativos" element={<Detalhes />} />
          <Route
            path="inscricoes-encerradas"
            element={<InscricaoEncerrada />}
          />
          <Route
            path="inscricoes-em-breve"
            element={<InscricaoEncerrada />}
          />
          <Route
            path="inscricoes/:type/inscricao-concluida/:id"
            element={<InscricaoConcluida />}
          />
          <Route
            path=":type/inscricao-concluida/:id"
            element={<InscricaoConcluida />}
          />
          <Route
            path="inscricoes/voluntario"
            element={<FormInscricao type="voluntário" />}
          />
          <Route
            path="voluntario"
            element={<FormInscricao type="voluntário" />}
          />
          <Route
            path="inscricoes/participante"
            element={<FormInscricao type="participante" />}
          />
          <Route
            path="participante"
            element={<FormInscricao type="participante" />}
          />
          <Route
            path="inscricoes/participante/pagamento/:participanteId"
            element={<FormInscricao type="participante" />}
          />
          <Route
            path="participante/pagamento/:participanteId"
            element={<FormInscricao type="participante" />}
          />
          <Route
            path="inscricoes/voluntario/pagamento/:participanteId"
            element={<FormInscricao type="voluntário" />}
          />
          <Route
            path="voluntario/pagamento/:participanteId"
            element={<FormInscricao type="voluntário" />}
          />
          <Route path="*" element={<NaoAutorizado />} />
        </Route>
      )}


      <Route path="/auth/sso/callback" element={<SsoCallback />} />
      <Route
        path="/sistema/biblia"
        element={
          <Suspense fallback={<Loading />}>
            <BibliaPassagem />
          </Suspense>
        }
      />
      <Route path="/recuperar-senha" element={<RecuperarSenha />} />
      <Route path="/recuperar-senha/:userId" element={<NewPassword />} />
      <Route path="/newUser/:userId" element={<NewUser />} />
      <Route path="naoAutorizado" element={<NaoAutorizado />} />
      <Route path="/sistema" element={<PrivateRoutes />}>
        <Route index element={handleHome()} />
        <Route path="backoffice/:tab?" element={<Backoffice />} />
        <Route path="listagem" element={<Listagem />} />
        <Route path="busca" element={<BuscaGlobalPage />} />
        <Route path="reunioes" element={<Reunioes />} />
        <Route
          path="programacao"
          element={
            <featureFlag.FeatureRoute flag="Programacao">
              <Programacao />
            </featureFlag.FeatureRoute>
          }
        />
        <Route
          path="programacao/momento/:momentoId"
          element={
            <featureFlag.FeatureRoute flag="Programacao">
              <MomentoCoordenador />
            </featureFlag.FeatureRoute>
          }
        />
        <Route path="participantes" element={<Participantes />} />
        <Route path="voluntarios" element={<Voluntarios />} />
        <Route path="equipes" element={<RedirecionarVoluntariosAba aba="equipes" />} />
        <Route path="distribuicao/participantes" element={<DistribuicaoParticipantesRoute />} />
        <Route path="distribuicao/voluntarios" element={<DistribuicaoVoluntariosRoute />} />
        {/* Telas antigas de Quartos, Círculos, Padrinhos, Caronas e Dirigentes viraram abas de Participantes. */}
        <Route path="padrinhos" element={<RedirecionarDistribuicao pessoas="participantes" modulo="padrinhos" />} />
        <Route path="circulos" element={<RedirecionarParticipantesAba aba="circulos" />} />
        {/* Auditoria item 5: /sistema/quartos e /sistema/relatorios nao tinham rota e caiam na pagina
            publica /:login/:identificador (evento "quartos" da organizacao "sistema"), que ficava carregando. */}
        <Route path="quartos" element={<RedirecionarDistribuicao pessoas="participantes" modulo="quartos" />} />
        <Route path="relatorios" element={<Navigate to="/sistema/aniversariantes" replace />} />
        <Route path="quartos/voluntarios" element={<RedirecionarDistribuicao pessoas="voluntarios" modulo="quartos" />} />
        <Route path="quartos/participantes" element={<RedirecionarDistribuicao pessoas="participantes" modulo="quartos" />} />
        <Route path="caronas" element={<RedirecionarDistribuicao pessoas="participantes" modulo="caronas" />} />
        <Route path="produtos" element={<Produtos />} />
        <Route path="presenca" element={<RedirecionarVoluntariosAba aba="presenca" />} />
        <Route path="marcadores" element={<Marcadores />} />
        <Route path="mensagens" element={<Mensagens />} />
        <Route
          path="cupons"
          element={
            <featureFlag.FeatureRoute flag="CheckoutTransparente">
              <Cupons />
            </featureFlag.FeatureRoute>
          }
        />
        <Route path="coordenador" element={<RedirecionarVoluntariosAba aba="coordenador" />} />
        <Route path="dirigentes" element={<RedirecionarParticipantesAba aba="dirigente" />} />
        <Route path="aniversariantes" element={<Aniversariantes />} />
        <Route path="restricao-alimentar" element={<RestricaoAlimentar />} />
        <Route path="relatorio-financeiro" element={<Financeiro />} />
        <Route path="voluntariado" element={<Voluntariado />} />
        <Route path="credenciais" element={<Credenciais />} />
        <Route path="retrospectiva" element={<Home />} />
        <Route
          path="extrato"
          element={
            <EnsinoPermissionRouteConditional>
              <Extrato />
            </EnsinoPermissionRouteConditional>
          }
        />
        <Route path="eventos" element={<Eventos />} />
        <Route path="turmas" element={<Turmas />} />
        <Route
          path="cursos"
          element={
            <featureFlag.FeatureRoute flag="Ensino">
              <Cursos />
            </featureFlag.FeatureRoute>
          }
        />
        <Route
          path="documentos"
          element={
            <featureFlag.FeatureRoute flag="Ensino">
              <Certificados />
            </featureFlag.FeatureRoute>
          }
        />

        <Route
          path="alunos/turmas"
          element={
            <featureFlag.FeatureRoute flag="Ensino">
              <ListagemParticipantes />
            </featureFlag.FeatureRoute>
          }
        />
        <Route
          path="alunos/geral"
          element={
            <featureFlag.FeatureRoute flag="Ensino">
              <ListagemGeralAlunos />
            </featureFlag.FeatureRoute>
          }
        />

        <Route path="pequenos-grupos" element={<PequenosGrupos />} />
        <Route path="disciplinas" element={<Disciplinas />} />
        <Route path="pedidos-oracao" element={
            <OracaoPermissionRoute>
              <ListagemPedidosOracao />
            </OracaoPermissionRoute>
          }
        />
        <Route path="plus/assinaturas" element={<PlusAssinaturasPage />} />
        <Route path="plus/planos" element={<PlusPlanosPage />} />
        <Route path="plus/cupons" element={<PlusCuponsPage />} />
        <Route path="plus/clube" element={<PlusClubePage />} />
        <Route path="plus/campanhas" element={<PlusCampanhasPage />} />
        <Route path="questionarios" element={<QuestionariosListaPage />} />
        <Route path="questionarios/lgpd" element={<QuestionariosLgpdPage />} />
        <Route path="questionarios/cupons" element={<QuestionariosCuponsPage />} />
        <Route path="questionarios/:id" element={<QuestionarioDetalhePage />} />
        <Route path="liderancas" element={<Liderancas />} />
        <Route path="liderancas/listagem" element={<ListagemLiderancas />} />
        <Route path="arvore-liderancas" element={<ArvoreLiderancas />} />
        <Route path="jornada" element={<Jornada />} />
        <Route path="avaliacoes/templates" element={<Templates />} />
        <Route path="avaliacoes/listagem" element={<ListagemAvaliacoes />} />
        <Route path="checkin" element={<Checkin />} />
        <Route path="parametros" element={<Parametros />} />
        <Route path="organizacao" element={<Organizacao />} />
        <Route path="perfil-instituicao" element={<PerfilInstituicao />} />
        <Route path="agenda" element={<Agenda />} />
        <Route path="locais" element={<Locais />} />
        <Route path="informativos" element={<Informativos />} />
        <Route path="notificacoes-push" element={<NotificacoesPushPage />} />
        <Route path="usuarios" element={<Organizadores />} />
        <Route path="ajuda" element={<Ajuda />} />
        <Route path="notificacoes" element={<NotificacoesCentral />} />
        <Route path="conquistas" element={<ConquistasCentral />} />
        <Route path="newsletter" element={<Newsletter />} />
        <Route path="newsletter/:identificador" element={<NewsletterArtigo />} />
        <Route path="naoAutorizado" element={<NaoAutorizado />} />
        <Route path="ministerios/:id" element={<MinisterioDetalhe />} />
        <Route path="ministerios" element={<Ministerios />} />
        <Route path="armazenamento" element={<Arquivos />} />
        <Route path="campos" element={<Campos />} />
        <Route path="links" element={<Links />} />
        <Route
          path="site-igreja"
          element={
            <featureFlag.FeatureRoute flag="SiteDaIgreja">
              <SiteIgrejaEditor />
            </featureFlag.FeatureRoute>
          }
        />
        <Route path="financeiro" element={<FinanceiroGeral />} />
        <Route path="vendas" element={<Vendas />} />
        <Route path="infantil/criancas" element={<featureFlag.InfantilPermissionRoute tela="Crianças"><ListagemCriancasInfantil /></featureFlag.InfantilPermissionRoute>} />
        <Route path="infantil/cadastro-familia" element={<featureFlag.InfantilPermissionRoute tela="Cadastro de família"><CadastroFamiliaInfantil /></featureFlag.InfantilPermissionRoute>} />
        <Route path="infantil/momentos" element={<featureFlag.InfantilPermissionRoute tela="Momentos do culto"><MomentosCultoInfantil /></featureFlag.InfantilPermissionRoute>} />
        <Route path="infantil/config" element={<Navigate to="/sistema/parametros?moduloInfantil=true" replace />} />
        <Route path="infantil/credenciais" element={<featureFlag.InfantilPermissionRoute tela="Credenciais infantis"><Credenciais /></featureFlag.InfantilPermissionRoute>} />
        <Route path="infantil/operacao" element={<featureFlag.InfantilPermissionRoute tela="Operação do culto"><OperacaoCultoInfantil /></featureFlag.InfantilPermissionRoute>} />
        <Route path="infantil/familia" element={<InfantilFamiliaPage />} />
        <Route
          path="indicadores"
          element={
            <featureFlag.FeatureRoute flag="AnalyticsIA">
              <Indicadores />
            </featureFlag.FeatureRoute>
          }
        />
        <Route path="devocionais" element={<Devocionais />} />
        <Route path="devocionais/meus-checkins" element={<MeusCheckinsParidade />} />
        <Route path="devocionais/grupos" element={<Grupos />} />
        <Route path="devocionais/planos" element={<PlanosLeituraParidade />} />
        <Route path="devocionais/grupos/:id" element={<DetalhesGrupo />} />
        <Route path="devocionais/grupos/:id/checkins/:checkinId" element={<DetalhesGrupo />} />
        <Route path="devocionais/grupos/:id/participantes/:userId" element={<DetalhesGrupo />} />
        <Route path="devocionais/checkins/:id" element={<DetalhesCheckin />} />
        <Route path="devocionais/videos/:id" element={<DevocionalVideoPage />} />

        <Route path="ensino" element={<EnsinoHomeRedirect />} />
        <Route path="ensino/turma-professor/:id" element={<DetalhesTurma />} />
        <Route path="ensino/turma-aluno/:id" element={<DetalhesTurma />} />

        {/* /sistema/<x> desconhecido: sem isto o "*" perde no ranking para /:login/:identificador. */}
        <Route path=":paginaInexistente" element={<NaoAutorizado />} />
        <Route path="*" element={<NaoAutorizado />} />
      </Route>
      <Route path="/privacidade" element={<Privacidade />} />
      <Route path="/termos" element={<Termos />} />
      <Route path="/devocional/:id" element={<DevocionalPublico />} />
      <Route path="/devocional/video/:id" element={<DevocionalVideoLinkCompartilhado />} />
      <Route path="/d/:id" element={<DevocionalLinkCurto />} />
      <Route path="/comparar/sysigreja-x-sympla-x-even3" element={<CompararSysigrejaPage />} />
      <Route path="/app" element={<AppDownload />} />
      <Route path="/app/:identificador" element={<AppDownload />} />
      <Route path="/download" element={<Navigate to="/app" replace />} />
      <Route path="/lancamento-app-rsvp" element={<LancamentoAppRsvp />} />

      {/* Rotas de callback OAuth Mercado Pago */}
      <Route path="/mercadopago/connect" element={<MercadoPagoConnect />} />
      <Route path="/mercadopago/oauth/success" element={<OAuthCallback />} />
      <Route path="/mercadopago/oauth/error" element={<OAuthCallback />} />
    </Router>
  );

  return (
    <ErrorBoundary
      FallbackComponent={ChunkLoadFallback}
      onError={handleRouteChunkError}
    >
      <Suspense fallback={<RouteSuspenseFallback />}>
        <ScrollToTop />
        {routerContent}
      </Suspense>
    </ErrorBoundary>
  );
}


